import React, { useState, useEffect, useCallback } from 'react';
import { Briefcase } from 'lucide-react';
import Layout from '../components/Layout';
import { recordGameResult } from '../gameStats';
// import MouseGlowEffect from '../MouseGlowEffect';
const ROWS = 6;
const COLS = 7;
const MAX_DEPTH = 7;

const createEmptyBoard = () => {
  return Array(ROWS).fill().map(() => Array(COLS).fill(null));
};

const checkWinner = (board) => {
  // Horizontal, vertical, and diagonal checks
  const checkDirection = (r, c, dr, dc) => {
    const currentPlayer = board[r][c];
    if (!currentPlayer) return false;

    for (let i = 1; i < 4; i++) {
      const row = r + i * dr;
      const col = c + i * dc;
      if (row >= ROWS || row < 0 || col >= COLS || col < 0 || board[row][col] !== currentPlayer) {
        return false;
      }
    }
    return true;
  };

  for (let row = 0; row < ROWS; row++) {
    for (let col = 0; col < COLS; col++) {
      if (
        checkDirection(row, col, 1, 0) || // Vertical
        checkDirection(row, col, 0, 1) || // Horizontal
        checkDirection(row, col, 1, 1) || // Diagonal down-right
        checkDirection(row, col, 1, -1)   // Diagonal down-left
      ) {
        return board[row][col];
      }
    }
  }
  return null;
};

const findEmptyRow = (board, col) => {
  for (let row = ROWS - 1; row >= 0; row--) {
    if (!board[row][col]) return row;
  }
  return -1;
};

const evaluateWindow = (window, player) => {
  const opponent = player === 'Red' ? 'Yellow' : 'Red';
  let score = 0;

  const playerCount = window.filter(cell => cell === player).length;
  const emptyCount = window.filter(cell => cell === null).length;

  if (playerCount === 4) score += 100;
  else if (playerCount === 3 && emptyCount === 1) score += 5;
  else if (playerCount === 2 && emptyCount === 2) score += 2;

  const opponentCount = window.filter(cell => cell === opponent).length;
  if (opponentCount === 3 && emptyCount === 1) score -= 4;

  return score;
};

const evaluateBoard = (board, player) => {
  let score = 0;

  // Horizontal
  for (let row = 0; row < ROWS; row++) {
    for (let col = 0; col < COLS - 3; col++) {
      const window = board[row].slice(col, col + 4);
      score += evaluateWindow(window, player);
    }
  }

  // Vertical
  for (let col = 0; col < COLS; col++) {
    for (let row = 0; row < ROWS - 3; row++) {
      const window = [board[row][col], board[row+1][col], board[row+2][col], board[row+3][col]];
      score += evaluateWindow(window, player);
    }
  }

  // Diagonal (positive slope)
  for (let row = 0; row < ROWS - 3; row++) {
    for (let col = 0; col < COLS - 3; col++) {
      const window = [board[row][col], board[row+1][col+1], board[row+2][col+2], board[row+3][col+3]];
      score += evaluateWindow(window, player);
    }
  }

  // Diagonal (negative slope)
  for (let row = 3; row < ROWS; row++) {
    for (let col = 0; col < COLS - 3; col++) {
      const window = [board[row][col], board[row-1][col+1], board[row-2][col+2], board[row-3][col+3]];
      score += evaluateWindow(window, player);
    }
  }

  // Prefer center column
  const centerColumn = board.map(row => row[COLS >> 1]);
  const centerCount = centerColumn.filter(cell => cell === player).length;
  score += centerCount * 3;

  return score;
};

const minimax = (board, depth, alpha, beta, maximizingPlayer) => {
  const winner = checkWinner(board);
  if (winner === 'Red') return 1000000;
  if (winner === 'Yellow') return -1000000;
  if (board.every(row => row.every(cell => cell !== null))) return 0;
  if (depth === 0) return evaluateBoard(board, 'Red');

  if (maximizingPlayer) {
    let maxEval = -Infinity;
    for (let col = 0; col < COLS; col++) {
      const row = findEmptyRow(board, col);
      if (row !== -1) {
        board[row][col] = 'Red';
        const evaluation = minimax(board, depth - 1, alpha, beta, false);
        board[row][col] = null;
        maxEval = Math.max(maxEval, evaluation);
        alpha = Math.max(alpha, evaluation);
        if (beta <= alpha) break;
      }
    }
    return maxEval;
  } else {
    let minEval = Infinity;
    for (let col = 0; col < COLS; col++) {
      const row = findEmptyRow(board, col);
      if (row !== -1) {
        board[row][col] = 'Yellow';
        const evaluation = minimax(board, depth - 1, alpha, beta, true);
        board[row][col] = null;
        minEval = Math.min(minEval, evaluation);
        beta = Math.min(beta, evaluation);
        if (beta <= alpha) break;
      }
    }
    return minEval;
  }
};

const findBestMove = (board) => {
  let bestScore = -Infinity;
  let bestMove = null;
  const centerColumn = Math.floor(COLS / 2);
  const columnOrder = [centerColumn, ...Array.from({length: COLS}, (_, i) => i).filter(i => i !== centerColumn)];

  for (const col of columnOrder) {
    const row = findEmptyRow(board, col);
    if (row !== -1) {
      board[row][col] = 'Red';
      const score = minimax(board, MAX_DEPTH, -Infinity, Infinity, false);
      board[row][col] = null;
      if (score > bestScore) {
        bestScore = score;
        bestMove = col;
      }
    }
  }
  return bestMove;
};

const FourInARow = () => {
  const [board, setBoard] = useState(createEmptyBoard());
  const [isPlayerTurn, setIsPlayerTurn] = useState(true);
  const [winner, setWinner] = useState(null);
  const [isDraw, setIsDraw] = useState(false);

  useEffect(() => {
    if (winner || isDraw) recordGameResult(winner);
  }, [winner, isDraw]);

  const handleClick = useCallback((col) => {
    if (winner || isDraw || !isPlayerTurn) return;

    const row = findEmptyRow(board, col);
    if (row === -1) return;

    const newBoard = board.map(r => [...r]);
    newBoard[row][col] = 'Yellow';
    setBoard(newBoard);

    const gameWinner = checkWinner(newBoard);
    if (gameWinner) {
      setWinner(gameWinner);
      return;
    }

    if (newBoard.every(row => row.every(cell => cell !== null))) {
      setIsDraw(true);
      return;
    }

    setIsPlayerTurn(false);
    setTimeout(() => {
      const aiMove = findBestMove(newBoard);
      if (aiMove !== null) {
        const aiRow = findEmptyRow(newBoard, aiMove);
        if (aiRow !== -1) {
          newBoard[aiRow][aiMove] = 'Red';
          setBoard(newBoard);

          const gameWinnerAfterAi = checkWinner(newBoard);
          if (gameWinnerAfterAi) {
            setWinner(gameWinnerAfterAi);
          } else if (newBoard.every(row => row.every(cell => cell !== null))) {
            setIsDraw(true);
          }
        }
      }
      setIsPlayerTurn(true);
    }, 100);
  }, [board, winner, isDraw, isPlayerTurn]);

  const resetGame = useCallback(() => {
    setBoard(createEmptyBoard());
    setIsPlayerTurn(true);
    setWinner(null);
    setIsDraw(false);
  }, []);

  return (
    <div className="flex flex-col items-center justify-center bg-gray-200 dark:bg-gray-900 p-4 sm:p-8 rounded-xl shadow-2xl w-full max-w-sm mx-auto mt-8">
      <h2 className="text-2xl sm:text-3xl font-bold text-blue-600 dark:text-blue-400 mb-4">4-In-A-Row</h2>
      <div className="grid grid-cols-7 gap-1 mb-4">
        {board.map((row, rowIndex) =>
          row.map((cell, colIndex) => (
            <button
              key={`${rowIndex}-${colIndex}`}
              className={`w-8 h-8 sm:w-10 sm:h-10 border-2 ${
                cell === 'Yellow' ? 'bg-yellow-500' : cell === 'Red' ? 'bg-red-500' : 'bg-gray-400 dark:bg-gray-700'
              }`}
              onClick={() => handleClick(colIndex)}
              disabled={!!winner || isDraw}
            />
          ))
        )}
      </div>
      <div className="mt-4 text-lg sm:text-xl font-semibold text-black dark:text-white text-center">
        {isDraw ? "It's a Draw!" :
         winner ? `${winner === 'Red' ? 'AI Wins!' : 'You Won!'}` :
         `${isPlayerTurn ? 'Your Turn (Yellow)' : 'AI is thinking... (Red)'}`}
      </div>
      {(winner || isDraw) && (
        <button
          onClick={resetGame}
          className="mt-4 px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white font-semibold rounded-lg shadow-md hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors duration-200"
        >
          Reset Game
        </button>
      )}
    </div>
  );
};

const Portfolio = () => {
  const [, setActiveSection] = useState('about');
  const workExperience = [
    {
      title: "Founding AI Engineer",
      company: "Stealth Startup (AI Agents and Infrastructure)",
      domain: "https://www.linkedin.com/company/stealthaistartup/",
      period: "Sep 2024 - Present",
      location: "Singapore",
      description: [
        "Architected domain-specific language (DSL) for defining applications via text files, reducing model integration time by > 90% and application code by 60%. Enabled zero-day support for new open-source models (2-minute integration) with built-in metrics, cutting production deployment time by 99%.",
        "Developed a GPU orchestration platform for managing entire end-to-end lifecycle of AI agents and applications. This platform connects machines from multi-cloud providers, and even bare metal machines, via WireGuard VPN. Enabled easy deployment and scaling of AI applications.",
        "Developed an enterprise knowledge base application with Retrieval Augmented Generation (RAG) capabilities. This was deployed on premise for a client, and used by them for knowledge sharing and retrieval. Designed solution to run on premise, to fulfill security and compliance requirements. Developed an email agent for clients to interface with AI agent via email.",
        "Automated lead generation pipeline for venture capital firms by designing AI research agents to research and identify potential leads that match investment criteria.",
        "Wrote a CLI tool for managing the GPU orchestration platform. Used by entire company for test automation, deployments and development.",
        "Designed and implemented an identity provider service handling authentication across all applications, securing user data and serving as core infrastructure dependency company-wide.",
        // "Unified agent infrastructure with compatible CUDA drivers across machine types, cutting pipeline costs >90%, reducing image generation runtime 70%, slashing storage costs 69%, and resolving critical boot stability bug.",
        "Added MX and CNAME record management capabilities to the core DNS service, enabling support for enterprise email services and hosting custom domains.",
        "Built scalable metrics infrastructure with custom Rust-based sidecar and monitoring dashboards, enabling real-time observability across critical systems.",
        "Accelerated RAG recipe performance through HTTP connection pooling (20% latency reduction) and streaming APIs (100x faster time-to-first-token). Reduced agent deployment from 20 minutes to 3 minutes using custom AWS AMIs and optimized build scripts."
      ],
      skills: ["Rust", "Go", "Python", "AWS", "HAProxy", "React", "HTML", "Jinja", "GCP", "Enterprise Compliance", "On Premise", "WireGuard"]
    },
    {
      title: "Backend Software Engineer",
      company: "TikTok",
      domain: "https://www.tiktok.com",
      period: "Aug 2021 - Aug 2024",
      location: "Singapore",
      description: [
        "Led department-wide optimization initiative for peak traffic, slashing downstream QPS by 50%, improving p50/p99 latency by 60%/70%, and saving an estimated $300k/year through architectural, query, and caching improvements. Designed high-performance generic caching SDK with single-line integration, doubling peak traffic handling capacity.",
        "Migrated department builds to Bazel, accelerating build speeds 10x (45min → 4.5min) and saving an estimated 100+ engineering hours monthly. Reduced deployment pipeline runtime by 60%, thereby improving overall development velocity.",
        "Developed cross-functional async export service, scaling capacity from 20K to 1M+ rows while eliminating large export errors. Achieved full adoption across all data products.",
        "Launched partner self-service custom reporting with flexible metric selection, date ranges, and aggregation methods. Built generic SQL query engine supporting 300+ different metric/dimension combinations with <3s p99 latency across TikTok's complete livestream dataset.",
        "Assisted with feature prioritisation, OKR planning and engineering capacity planning when the team's lead was away.",
        "Mentored 5 engineers across design reviews, code quality, and technical development.",
        "Conducted 100+ technical interviews (coding and system design) across 4 departments and 2 countries, supporting recruitment for critical roles."
      ],
      skills: ["Golang", "Python", "Bash", "Bazel", "Big Data", "Flink", "Kafka", "RocketMQ", "ClickHouse", "Docker", "MySQL", "Redis", "Hive"]
    },
    {
      title: "Software Engineer (Full Stack)",
      company: "LeanData Inc",
      domain: "https://leandata.com",
      period: "Aug 2019 - Aug 2020",
      location: "Sunnyvale, CA",
      description: [
        "Optimized app settings import/export by redesigning feature architecture to partition payloads across multiple transactions, scaling record handling capacity 10x.",
        "Implemented semaphore-based locking in Apex to prevent concurrency conflicts during simultaneous graph edits.",
        "Removed unused legacy assets, reducing application size by 30% and enabling closure of high-value contracts."
      ],
      skills: ["Java", "SQL", "Angular", "BackboneJS"]
    },
    {
      title: "Captain",
      company: "Singapore Armed Forces",
      domain: "https://www.ns.gov.sg",
      period: "2021 - Present",
      location: "Singapore",
      description: [
        "Planned and executed training programs and logistics operations for a battery of 100+ personnel, ensuring operational readiness and administrative efficiency."
      ],
      skills: ["Leadership", "Operations Management", "Process Optimization", "Communication"]
    }
  ];

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['experience'];
      const scrollPosition = window.scrollY + 100;
    
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };
  
    window.addEventListener('scroll', handleScroll);
    handleScroll();
  
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <Layout>
      <div id="content" className="max-w-5xl mx-auto px-4 md:px-8 xl:px-24 2xl:px-32">
            <div className="mb-12">
              <h1 className="text-4xl tracking-tight text-black dark:text-gray-200 sm:text-5xl mb-4">
                Experience
              </h1>
            </div>

            <section id="experience" className="mb-8 scroll-mt-16 md:mb-12 lg:mb-16 lg:scroll-mt-24" aria-label="Work experience">
              <div>
                <ol className="group/list">
                  {workExperience.map((job, index) => (
                    <li key={index} className="mb-12">
                      <div className="group relative grid pb-1 transition-all sm:grid-cols-8 sm:gap-8 md:gap-4 lg:hover:!opacity-100 lg:group-hover/list:opacity-50">
                        <div className="absolute -inset-x-4 -inset-y-4 z-0 hidden rounded-md transition motion-reduce:transition-none lg:-inset-x-6 lg:block lg:group-hover:bg-gray-200/50 dark:lg:group-hover:bg-gray-800/50 lg:group-hover:shadow-[inset_0_1px_0_0_rgba(148,163,184,0.1)] lg:group-hover:drop-shadow-lg"></div>
                        <header className="z-10 mb-2 mt-1 text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-500 sm:col-span-2">
                          {job.period}
                        </header>
                        <div className="z-10 sm:col-span-6">
                          <h3 className="font-medium leading-snug text-black dark:text-gray-200">
                            <div>
                              <a className="inline-flex items-center font-medium leading-tight text-black dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-300 focus-visible:text-blue-600 dark:focus-visible:text-blue-300 group/link text-base" href={job.domain} target="_blank" rel="noreferrer noopener">
                                <span className="absolute -inset-x-4 -inset-y-2.5 hidden rounded md:-inset-x-6 md:-inset-y-4 lg:block"></span>
                                <span>{job.title} · {job.company}</span>
                                {job.logoUrl ? (
                                  <img
                                    src={job.logoUrl}
                                    alt={`${job.company} logo`}
                                    className="inline-block ml-2 h-4 w-4 shrink-0 transition-transform group-hover/link:-translate-y-1 group-hover/link:translate-x-1 group-focus-visible/link:-translate-y-1 group-focus-visible/link:translate-x-1 motion-reduce:transition-none"
                                    style={{ verticalAlign: 'middle' }}
                                  />
                                ) : job.domain ? (
                                  <img
                                    src={`https://www.google.com/s2/favicons?domain=${job.domain}&sz=32`}
                                    alt={`${job.company} logo`}
                                    className="inline-block ml-2 h-4 w-4 shrink-0 transition-transform group-hover/link:-translate-y-1 group-hover/link:translate-x-1 group-focus-visible/link:-translate-y-1 group-focus-visible/link:translate-x-1 motion-reduce:transition-none"
                                    style={{ verticalAlign: 'middle' }}
                                  />
                                ) : (
                                  <Briefcase className="inline-block h-4 w-4 shrink-0 transition-transform group-hover/link:-translate-y-1 group-hover/link:translate-x-1 group-focus-visible/link:-translate-y-1 group-focus-visible/link:translate-x-1 motion-reduce:transition-none ml-1 translate-y-px" />
                                )}
                              </a>
                            </div>
                          </h3>
                          <ul className="mt-2 text-base leading-normal text-gray-600 dark:text-gray-400">
                            {job.description.map((point, pointIndex) => (
                              <li key={pointIndex} className="mb-2">
                                {point}
                              </li>
                            ))}
                          </ul>
                          <ul className="mt-2 flex flex-wrap" aria-label="Technologies used">
                            {job.skills.map((skill, skillIndex) => (
                              <li key={skillIndex} className="mr-1.5 mt-2">
                                <div className="flex items-center rounded-full bg-blue-600/10 dark:bg-blue-400/10 px-3 py-1 text-xs font-medium leading-5 text-blue-600 dark:text-blue-300">
                                  {skill}
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            </section>

            <section id="projects" className="mb-8 scroll-mt-16 md:mb-12 lg:mb-16 lg:scroll-mt-24" aria-label="Projects">
              <div className="sticky top-0 z-20 -mx-6 mb-4 w-screen bg-white/75 dark:bg-gray-900/75 px-6 py-5 backdrop-blur md:-mx-12 md:px-12 lg:relative lg:top-auto lg:w-full">
                <h2 className="text-xl uppercase tracking-widest text-black dark:text-gray-200">Bored?</h2>
              </div>
              <div>
                <div>
                  <FourInARow />
                </div>
              </div>
            </section>
      </div>
    </Layout>
  );
};

export default Portfolio;

